import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import { Building2, Globe, Plus } from 'lucide-react';
import type { IOrganizationSubscription } from '../interfaces';

interface IOrganizationsGridProps {
  data: IOrganizationSubscription[];
  onCardClick: (org: IOrganizationSubscription) => void;
  onQuickAdd?: () => void;
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'success';
    case 'trial':
    case 'pending':
      return 'warning';
    case 'cancelled':
    case 'expired':
      return 'error';
    default:
      return 'default';
  }
};

export const OrganizationsGrid: React.FC<IOrganizationsGridProps> = ({
  data,
  onCardClick,
  onQuickAdd,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        lg: 'repeat(3, 1fr)',
        xl: 'repeat(4, 1fr)',
      },
      gap: 3,
    }}
  >
    {data.map((org) => {
      const status = org.subscription.candidateFeature?.status ?? 'unknown';

      return (
        <Paper
          key={org.organization.id}
          variant="outlined"
          onClick={() => onCardClick(org)}
          sx={{
            p: 3,
            borderRadius: 4,
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            '&:hover': {
              boxShadow: 4,
              borderColor: 'primary.light',
              transform: 'translateY(-2px)',
            },
          }}
        >
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 3,
                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.main',
                }}
              >
                {org.organization.companyLogo ? (
                  <Box
                    component="img"
                    src={org.organization.companyLogo}
                    alt={org.organization.organizationName}
                    sx={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 1.5 }}
                  />
                ) : (
                  <Building2 size={24} />
                )}
              </Box>
              <Chip
                label={status}
                size="small"
                color={getStatusColor(status)}
                sx={{
                  fontWeight: 800,
                  fontSize: '0.6875rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderRadius: 2,
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 800,
                  color: 'text.primary',
                  letterSpacing: '-0.01em',
                  mb: 0.5,
                  '&:hover': { color: 'primary.main' },
                }}
              >
                {org.organization.organizationName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                <Globe size={14} />
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  {org.organization.primaryDomain}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}
                >
                  Company Size
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {org.subscription.companySize}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block' }}
                >
                  Interval
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', textTransform: 'capitalize' }}>
                  {org.subscription.interval}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      );
    })}

    <Paper
      variant="outlined"
      onClick={onQuickAdd}
      sx={{
        p: 3,
        borderRadius: 4,
        borderStyle: 'dashed',
        borderWidth: 2,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: 220,
        bgcolor: 'action.hover',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'rgba(16, 185, 129, 0.05)',
        },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
          boxShadow: 1,
        }}
      >
        <Plus size={24} color="#64748b" />
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', lineHeight: 1.3 }}>
        Quick add new
        <br />
        organization
      </Typography>
    </Paper>
  </Box>
);
